import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Button, Input, Separator, SegmentedTabs } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Badge } from "@/components/ui/badge";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { cn } from "@/lib/utils";
import {
  useCreatePrescriptionTemplate,
  useDeletePrescriptionTemplate,
  usePrescriptionTemplates,
  useUpdatePrescriptionTemplate,
  type PrescriptionTemplateWithItems,
} from "@/hooks/usePrescriptionTemplates";
import { useCreateMedication, useMedications } from "@/hooks/useMedications";
import {
  prescriptionTemplateSchema,
  type PrescriptionTemplateInput,
} from "@/lib/validations/prescription-template";
import type { PrescriptionItemInput } from "@/lib/validations/medical-prescription";
import type { Database } from "@/integrations/supabase/types";
import { MedicationAutocomplete, type MedicationWithRelations } from "./MedicationAutocomplete";
import { CreateMedicationDialog } from "./CreateMedicationDialog";
import { buildAutomaticPosologyDescription, buildPosologySummary } from "@/lib/prescriptions/posology";
import { doseAmountOptions } from "@/lib/prescriptions/posology-options";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

const ROUTE_OPTIONS = ["Oral", "Subcutânea", "Intramuscular", "Intravenosa", "Tópica", "Oftálmica", "Otológica", "Transdérmica"];
const DOSE_UNIT_OPTIONS = ["Comprimido", "Cápsula", "ml", "Gota", "Sachê", "Jato"];
const FREQUENCY_UNIT_OPTIONS = ["Hora", "Horas", "Dia", "Dias", "Semana", "Semanas", "Mês", "Meses"];
const DURATION_UNIT_OPTIONS = ["Dia", "Dias", "Semana", "Semanas", "Mês", "Meses"];

const routeOptions = ROUTE_OPTIONS.map((option) => ({ label: option, value: option }));
const doseUnitOptions = DOSE_UNIT_OPTIONS.map((option) => ({ label: option, value: option }));
const frequencyUnitOptions = FREQUENCY_UNIT_OPTIONS.map((option) => ({ label: option, value: option }));
const durationUnitOptions = DURATION_UNIT_OPTIONS.map((option) => ({ label: option, value: option }));

const buildDefaultItem = (): PrescriptionItemInput => ({
  medication_id: "",
  medication_name: "",
  medication_concentration: "",
  pharmaceutical_form: "",
  administration_route: "",
  presentation: "",
  posology_mode: "automatic",
  free_text_posology: "",
  dose_amount: "",
  dose_unit: "",
  frequency_value: "",
  frequency_unit: "",
  duration_value: "",
  duration_unit: "",
});

function getLookupName(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
}

const mapTemplateItems = (template: PrescriptionTemplateWithItems): PrescriptionItemInput[] => {
  if (!template.items.length) return [buildDefaultItem()];

  return template.items.map((item) => ({
    medication_id: item.medication_id ?? "",
    medication_name: item.medication_name ?? "",
    medication_concentration: item.medication_concentration ?? "",
    pharmaceutical_form: item.pharmaceutical_form ?? "",
    administration_route: item.administration_route ?? "",
    presentation: item.presentation ?? "",
    posology_mode: item.posology_mode ?? "automatic",
    free_text_posology: item.free_text_posology ?? "",
    dose_amount: item.dose_amount ?? "",
    dose_unit: item.dose_unit ?? "",
    frequency_value: item.frequency_value ?? "",
    frequency_unit: item.frequency_unit ?? "",
    duration_value: item.duration_value ?? "",
    duration_unit: item.duration_unit ?? "",
  }));
};

interface PrescriptionTemplatesTabProps {
  selectedTemplateId?: string | null;
  onTemplateApplied?: () => void;
  showTemplatesList?: boolean;
}

export const PrescriptionTemplatesTab = ({
  selectedTemplateId,
  onTemplateApplied,
  showTemplatesList = true,
}: PrescriptionTemplatesTabProps) => {
  const { t } = useTranslation("prescriptions");
  const { data: clinicId } = useCurrentClinicId();
  const { data: templates = [], isLoading: isLoadingTemplates } = usePrescriptionTemplates(clinicId, "standard");
  const createTemplate = useCreatePrescriptionTemplate();
  const updateTemplate = useUpdatePrescriptionTemplate();
  const deleteTemplate = useDeletePrescriptionTemplate();
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<PrescriptionTemplateWithItems | null>(null);
  const [showCreateMedicationDialog, setShowCreateMedicationDialog] = useState(false);
  const [medicationNameToCreate, setMedicationNameToCreate] = useState("");
  const [itemModes, setItemModes] = useState<Record<number, "structured" | "freeText">>({});

  const form = useForm<PrescriptionTemplateInput>({
    resolver: zodResolver(prescriptionTemplateSchema),
    defaultValues: {
      title: "",
      observations: "",
      template_type: "standard",
      items: [buildDefaultItem()],
    },
  });

  const { fields: itemFields, append, remove, replace, update: updateItem } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const [medicationSearch, setMedicationSearch] = useState("");
  const { data: medications = [], isLoading: isLoadingMedications } = useMedications({
    searchTerm: medicationSearch,
    includeControlled: true,
  });
  const createMedication = useCreateMedication();

  const medicationsById = useMemo(() => new Map(medications.map((med) => [med.id, med])), [medications]);

  const handleMedicationSelection = (index: number, medication: MedicationRow, displayName?: string) => {
    const typedMedication = medication as unknown as MedicationWithRelations;
    updateItem(index, {
      ...form.getValues(`items.${index}`),
      medication_id: medication.id,
      medication_name: displayName ?? medication.name,
      medication_concentration: medication.concentration ?? "",
      pharmaceutical_form: getLookupName(typedMedication.pharmaceutical_form),
      administration_route: getLookupName(typedMedication.administration_route),
      dose_unit: form.getValues(`items.${index}.dose_unit`) || medication.dose_unit || "",
    });

    form.clearErrors(`items.${index}.medication_name`);
  };

  const handleOpenCreateMedicationDialog = (name: string) => {
    setMedicationNameToCreate(name);
    setShowCreateMedicationDialog(true);
  };

  const handleMedicationCreated = (medication: MedicationRow) => {
    const currentIndex = itemFields.length - 1;
    if (currentIndex >= 0) handleMedicationSelection(currentIndex, medication, medication.name);
    setMedicationSearch("");
  };

  const resetForm = () => {
    form.reset({
      title: "",
      observations: "",
      template_type: "standard",
      items: [buildDefaultItem()],
    });
    replace([buildDefaultItem()]);
    setEditingTemplateId(null);
    setMedicationSearch("");
    setItemModes({});
  };

  const handleEditTemplate = useCallback((template: PrescriptionTemplateWithItems) => {
    const mappedItems = mapTemplateItems(template);
    form.reset({
      title: template.title ?? "",
      observations: template.observations ?? "",
      template_type: "standard",
      items: mappedItems,
    });
    replace(mappedItems);
    setEditingTemplateId(template.id);
    setMedicationSearch("");
    setItemModes(
      Object.fromEntries(
        mappedItems.map((item, index) => [index, item.posology_mode === "free_text" ? "freeText" : "structured"]),
      ),
    );
  }, [form, replace]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) return;
    handleEditTemplate(template);
    onTemplateApplied?.();
  }, [handleEditTemplate, onTemplateApplied, selectedTemplateId, templates]);

  const handleSubmit = async (values: PrescriptionTemplateInput) => {
    if (!clinicId) return;
    if (editingTemplateId) {
      await updateTemplate.mutateAsync({ templateId: editingTemplateId, clinicId, payload: values });
    } else {
      await createTemplate.mutateAsync({ ...values, clinic_id: clinicId });
    }
    resetForm();
  };

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === editingTemplateId) ?? null,
    [editingTemplateId, templates],
  );

  return (
    <div className={cn(showTemplatesList ? "grid gap-6 md:grid-cols-[2fr,1fr] md:items-start" : "space-y-6")}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 order-last md:order-first">
          <Card>
            <CardHeader>
              <CardTitle>{t("templates.standardTitle")}</CardTitle>
              <CardDescription>{t("templates.standardDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("templates.templateTitle")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("templates.standardTitlePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                {itemFields.map((item, index) => {
                  const itemPath = `items.${index}` as const;
                  const currentItem = form.watch(itemPath);
                  const isStructured = (itemModes[index] ?? "structured") === "structured";
                  return (
                    <div key={item.id} className="rounded-lg border border-dashed border-border/70 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-4">
                          <Controller
                            control={form.control}
                            name={`items.${index}.medication_name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("templates.medication")}</FormLabel>
                                <FormControl>
                                  <MedicationAutocomplete
                                    medications={medications as MedicationWithRelations[]}
                                    isLoading={isLoadingMedications}
                                    value={field.value ?? ""}
                                    selectedMedicationId={form.watch(`items.${index}.medication_id`) ?? ""}
                                    onChange={(value) => {
                                      setMedicationSearch(value);
                                      const current = form.getValues(`items.${index}`);
                                      if (current.medication_id) {
                                        updateItem(index, {
                                          ...current,
                                          medication_id: "",
                                          medication_name: value,
                                          medication_concentration: "",
                                          pharmaceutical_form: "",
                                          administration_route: "",
                                          presentation: "",
                                        });
                                      } else {
                                        field.onChange(value);
                                      }
                                    }}
                                    onSelectMedication={(medication, displayName) => {
                                      setMedicationSearch("");
                                      field.onChange(displayName);
                                      handleMedicationSelection(index, medication, displayName);
                                    }}
                                    onOpenCreateDialog={handleOpenCreateMedicationDialog}
                                    placeholder={t("templates.medicationPlaceholder")}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <SegmentedTabs
                            value={itemModes[index] ?? "structured"}
                            onValueChange={(value) => {
                              setItemModes((current) => ({
                                ...current,
                                [index]: value as "structured" | "freeText",
                              }));
                              form.setValue(`items.${index}.posology_mode`, value === "freeText" ? "free_text" : "automatic");
                            }}
                            fill="screen"
                            items={[
                              { value: "structured", label: t("composer.structuredFieldsTab") },
                              { value: "freeText", label: t("composer.freeTextTab") },
                            ]}
                          />

                          {isStructured ? (
                            <>
                              <div className="space-y-4 rounded-xl border bg-muted/20 p-4">
                                <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.dose_amount`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("templates.dosage")}</FormLabel>
                                          <FormControl>
                                            <Autocomplete
                                              options={doseAmountOptions}
                                              value={field.value ?? ""}
                                              onChange={field.onChange}
                                              placeholder={t("composer.doseAmountPlaceholder")}
                                              emptyMessage={t("composer.noSuggestions")}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.dose_unit`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("composer.doseUnit")}</FormLabel>
                                          <FormControl>
                                            <Autocomplete
                                              options={doseUnitOptions}
                                              value={field.value ?? ""}
                                              onChange={field.onChange}
                                              placeholder={t("composer.selectOrType")}
                                              emptyMessage={t("composer.noSuggestions")}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  <div className="grid gap-3 md:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.frequency_value`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("templates.frequency")}</FormLabel>
                                          <FormControl>
                                            <Input placeholder={t("composer.frequencyValuePlaceholder")} {...field} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.frequency_unit`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("composer.frequencyUnit")}</FormLabel>
                                          <FormControl>
                                            <Autocomplete
                                              options={frequencyUnitOptions}
                                              value={field.value ?? ""}
                                              onChange={field.onChange}
                                              placeholder={t("composer.selectOrType")}
                                              emptyMessage={t("composer.noSuggestions")}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </div>

                                <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.duration_value`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("templates.duration")}</FormLabel>
                                          <FormControl>
                                            <Input placeholder={t("composer.durationValuePlaceholder")} {...field} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.duration_unit`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("composer.durationUnit")}</FormLabel>
                                          <FormControl>
                                            <Autocomplete
                                              options={durationUnitOptions}
                                              value={field.value ?? ""}
                                              onChange={field.onChange}
                                              placeholder={t("composer.selectOrType")}
                                              emptyMessage={t("composer.noSuggestions")}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  <div className="grid gap-3 md:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`items.${index}.administration_route`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t("templates.route")}</FormLabel>
                                          <FormControl>
                                            <Autocomplete
                                              options={routeOptions}
                                              value={field.value ?? ""}
                                              onChange={field.onChange}
                                              placeholder={t("templates.routePlaceholder")}
                                              emptyMessage={t("composer.noSuggestions")}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </div>

                                <div className="rounded-lg border bg-background p-3 text-sm text-primary shadow-sm">
                                  <span className="font-semibold">{t("composer.finalDescriptionLabel")}: </span>
                                  {buildAutomaticPosologyDescription(currentItem, (key, options) => String(t(`print.${key}`, options)))}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="space-y-3">
                              <FormField
                                control={form.control}
                                name={`items.${index}.administration_route`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t("templates.route")}</FormLabel>
                                    <FormControl>
                                      <Autocomplete
                                        options={routeOptions}
                                        value={field.value ?? ""}
                                        onChange={field.onChange}
                                        placeholder={t("templates.routePlaceholder")}
                                        emptyMessage={t("composer.noSuggestions")}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`items.${index}.free_text_posology`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t("composer.freeTextFieldLabel")}</FormLabel>
                                    <FormControl>
                                      <AutoGrowTextarea
                                        placeholder={t("composer.freeTextFieldPlaceholder")}
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}
                        </div>

                        {itemFields.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" className="mt-1 shrink-0 text-muted-foreground" onClick={() => remove(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button type="button" variant="outline" className="w-full" onClick={() => append(buildDefaultItem())}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("composer.addMedication")}
                </Button>
              </div>

              <FormField
                control={form.control}
                name="observations"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>{t("templates.complementaryGuidance")}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t("templates.complementaryGuidancePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {editingTemplateId ? (
              <Button type="button" variant="outline" className="shadow-soft-search" onClick={resetForm}>
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.cancelEdit")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={resetForm}
                disabled={!clinicId || createTemplate.isPending || updateTemplate.isPending}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.clear")}
              </Button>
            )}

            <Button type="submit" className="w-full sm:w-auto" disabled={!clinicId || createTemplate.isPending || updateTemplate.isPending}>
              {(createTemplate.isPending || updateTemplate.isPending) ? (
                <Loader2 className="!h-5 !w-5 animate-spin" />
              ) : (
                <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              )}
              <span className="font-normal">{editingTemplateId ? t("templates.actions.update") : t("templates.actions.save")}</span>
            </Button>
          </div>
        </form>
      </Form>

      {showTemplatesList && (
        <Card className="order-first md:order-last">
          <CardHeader>
            <CardTitle>{t("templates.registeredTemplates")}</CardTitle>
            {activeTemplate && <CardDescription>{t("templates.actions.editing", { title: activeTemplate.title })}</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-4 md:max-h-[500px]">
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("composer.loadingTemplates")}
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("templates.empty.standard")}</p>
            ) : (
              <div className="space-y-3 text-sm">
                {templates.map((template) => (
                  <div key={template.id} className="rounded-md border border-border/70 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium">{template.title}</div>
                          <Badge variant="secondary">{t("composer.templateTagStandard")}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("templates.counts.medications", { count: template.items.length })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditTemplate(template)} aria-label={t("templates.editTemplate")}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setTemplateToDelete(template)}
                          aria-label={t("templates.deleteTemplate")}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {template.items.length > 0 && (
                      <>
                        <Separator className="my-2" />
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          {template.items.slice(0, 3).map((item) => (
                            <li key={item.id}>
                              {item.medication_name} · {buildPosologySummary(item, (key, options) => String(t(`print.${key}`, options)))}
                            </li>
                          ))}
                        </ul>
                        {template.items.length > 3 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t("templates.counts.moreItems", { count: template.items.length - 3 })}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {templateToDelete && (
        <ConfirmationDialog
          open={!!templateToDelete}
          onOpenChange={(open) => {
            if (!open) setTemplateToDelete(null);
          }}
          title={t("templates.actions.deleteTitle")}
          description={t("templates.actions.deleteDescription", { title: templateToDelete.title })}
          confirmText={t("templates.actions.delete")}
          cancelText={t("templates.actions.cancel")}
          variant="destructive"
          onConfirm={() => {
            if (!templateToDelete || !clinicId) return;
            deleteTemplate.mutate({ templateId: templateToDelete.id, clinicId });
            setTemplateToDelete(null);
          }}
        />
      )}

      <CreateMedicationDialog
        open={showCreateMedicationDialog}
        onOpenChange={setShowCreateMedicationDialog}
        initialName={medicationNameToCreate}
        onMedicationCreated={handleMedicationCreated}
      />
    </div>
  );
};
